import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { sortFundNames } from '../../../../../utilities/customSorting';
import { SkeletonLoader } from '../../../../ui/containers/SkeletonLoader';
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  ColumnDef,
  FilterFn,
} from '@tanstack/react-table';
import { rankItem } from '@tanstack/match-sorter-utils';
import Pill from '../../../../ui/Pill';
import { HiChevronDown, HiChevronRight } from 'react-icons/hi2';
import { useDispatch, useSelector } from 'react-redux';
import {
  toggleControlBarShowing,
  updateControlBarComponents,
} from '../../../../../redux/ui/controlbar/actions';

import SearchBar from '../../../../ui/inputs/SearchBar';
import TokenService from '../../../../../services/token-service';
import { useFunds } from '../../../../../api/funds';
import { useAllShareClasses } from '../../../../../api/shareclass';
import {
  createPageDetailsSelector,
  createSectionByIdSelector,
} from '../../../../../redux/pages/selectors';
import { raptorPlatformIdSelector } from '../../../../../redux/raptorPlatform/selectors';
import {
  useGetLatestKidOverview,
  useGetKidTemplateIncompleteDocuments,
  useKidJurisdictions,
  useKidLanguages,
} from '../../../../../api/kids/hooks/queries';
import { useGenerateKidDocument } from '../../../../../api/kids/hooks/mutations';
import { getLatestMajorPublishedVersion } from '../../../../../api/kids/kids.routes';
import { UpdatedKidDataModel } from '../../../../../api/kids/types';
import type { KidDocumentForm } from '../types/KidPdfGeneratorV2.types';
import { createResourceCacheKey } from '../../../../layout/general/SimpleGeneralPageWrapper';
import { ExpandedState } from '@tanstack/react-table';
import countryCodesToNames from '../../../../../utilities/countryCodesToNames.json';
import FiguresAlert from '../components/FiguresAlert';
import KidToolBar from '../components/KidToolBar';
import { IconButton } from '../components/buttons';
import type { ColumnAlignMeta } from '../../../../tables/tanstack/tanstackUtils';
import {
  KidDocumentStatus,
  KidFund,
  KidShareClass,
} from '../types/KidEditorV2.types';
import type { ShareClassModel } from '../../../../../api/shareclass/types';
import ShareClassTableRow from '../components/ShareClassTableRow';
import { sanitiseFileName } from '../../../../../utilities/helpers/common/sanitisers';
import { transformApiDataToFunds, transformFunds, transformKidDataModelsToElements } from '../utils/kidDataTransformers';
import { isShareClassBlockedByTemplateIncomplete } from '../utils/templateIncomplete.utils';

// Nested fuzzy filter that searches both fund-level and share class-level data
const nestedFuzzyFilter: FilterFn<KidFund> = (row, columnId, value, addMeta) => {
  const fund = row.original as KidFund;
  const searchValue = String(value).toLowerCase();

  // Helper to check field match with rankItem
  const checkFieldMatch = (field: string, threshold = 6) => {
    const rank = rankItem(field, searchValue, { threshold } as any);
    if (rank.passed) {
      addMeta({ itemRank: rank });
      return true;
    }
    return false;
  };

  // Helper for substring matching (ISIN, names)
  const checkSubstringMatch = (field: string) => {
    const fieldLower = String(field).toLowerCase();
    if (fieldLower.includes(searchValue)) {
      return checkFieldMatch(fieldLower, 0);
    }
    return false;
  };

  // Search fund-level properties
  const fundFields = [
    fund.fund_name_full,
    fund.fund_name,
    fund.fund_id,
    fund.editStatus,
    fund.reviewStatus,
    fund.publishedStatus,
  ].filter(Boolean);

  if (
    fundFields.some((field) => checkFieldMatch(String(field).toLowerCase(), 3))
  ) {
    return true;
  }

  // Search share class-level properties
  for (const sc of fund.shareClasses) {
    // Check ISIN with substring matching first, then fuzzy
    if (
      sc.share_class_isin &&
      (checkSubstringMatch(sc.share_class_isin) ||
        checkFieldMatch(String(sc.share_class_isin).toLowerCase()))
    ) {
      return true;
    }
    // Check share class names with substring matching
    const scDisplayName = sc.share_class_name_full || sc.share_class_name;
    if (scDisplayName && checkSubstringMatch(scDisplayName)) {
      return true;
    }
  }

  return false;
};


export const getStatusCounts = (funds: KidFund[]) => {
  const counts = {
    editing: {
      editing: 0,
      rejected: 0,
    },
    review: {
      pendingReview: 0,
    },
    publish: {
      readyToPublish: 0,
    },
    alerts: {
      figuresAlert: 0,
      shareClassOutOfSync: 0,
    },
  };

  funds.forEach((fund) => {
    fund.shareClasses.forEach((sc) => {
      if (sc.status === KidDocumentStatus.Editing) {
        counts.editing.editing++;
      }
      if (sc.status === KidDocumentStatus.Rejected) {
        counts.editing.rejected++;
      }
      // "Edited" (mapped from API 'ready-for-review') drives the review bucket
      if (
        sc.status === KidDocumentStatus.Edited &&
        !isShareClassBlockedByTemplateIncomplete(sc)
      ) {
        counts.review.pendingReview++;
      }
      // "Reviewed" (mapped from API 'ready-to-publish') drives the publish bucket
      if (
        sc.status === KidDocumentStatus.Reviewed &&
        !isShareClassBlockedByTemplateIncomplete(sc)
      ) {
        counts.publish.readyToPublish++;
      }
      // Count alerts based on hasAlert property instead of status
      if (sc.has_alert) counts.alerts.figuresAlert++;
      if (sc.has_shareclass_out_of_sync)
        counts.alerts.shareClassOutOfSync++;
    });
  });

  return counts;
};

const KidsOverviewV2Page: FunctionComponent = () => {
  const { user_client_id: clientId } = TokenService.getUser()!;

  const dispatch = useDispatch();
  const location = useLocation();
  const hasRestoredRef = useRef(false);
  const [fundSearch, setFundSearch] = useState('');
  const [shareClassStatusFilter, setShareClassStatusFilter] = useState<
    'all' | KidDocumentStatus | 'shareclass-out-of-sync'
  >('all');
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [selectedJurisdiction, setSelectedJurisdiction] = useState<string>('');
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');

  // Fetch jurisdictions and languages
  const { data: jurisdictions, isLoading: isLoadingJurisdictions } =
    useKidJurisdictions();
  const { data: languages, isLoading: isLoadingLanguages } = useKidLanguages();

  // Map jurisdiction codes to names
  const jurisdictionOptions = useMemo(() => {
    if (!jurisdictions) return [];
    return jurisdictions
      .map((code) => ({
        code,
        name: (countryCodesToNames as Record<string, string>)[code] || code,
      }))
      .filter((country) => country.name)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [jurisdictions]);

  // Set initial values to first item in arrays when data loads
  useEffect(() => {
    if (jurisdictionOptions.length > 0 && !selectedJurisdiction) {
      setSelectedJurisdiction(jurisdictionOptions[0].code);
    }
  }, [jurisdictionOptions, selectedJurisdiction]);

  useEffect(() => {
    if (languages && languages.length > 0 && !selectedLanguage) {
      setSelectedLanguage(languages[0]);
    }
  }, [languages, selectedLanguage]);

  const sectionDetails = useSelector(createSectionByIdSelector('raidr'));
  const pageDetails = useSelector(
    createPageDetailsSelector('kid-overview-v2', 'raidr'),
  );

  const raptorPlatformId = useSelector(raptorPlatformIdSelector);

  const { data: allShareClasses } = useAllShareClasses({
    client_id: clientId,
  });


  const rsResources = createResourceCacheKey(
    sectionDetails?.rsResources,
    pageDetails?.rsResources,
  );

  const { data: totalFunds } = useFunds({
    fundTypes: sectionDetails?.fundTypes,
    rsResources,
    raptorPlatformId,
  });

  const {
    data: fundsData,
    isLoading,
    error,
    isError,
  } = useGetLatestKidOverview(clientId);

  const { data: incompleteDocuments } = useGetKidTemplateIncompleteDocuments(
    clientId,
  );

  // Transform flat API data to grouped fund structure, then to internal format
  const fundsWithKids = useMemo(() => {
    const groupedFunds = transformApiDataToFunds(fundsData);
    return transformFunds(groupedFunds);
  }, [fundsData]);

  // Merge funds from totalFunds (all funds) with funds that have KID documents.
  // Fund IDs (UUIDs) come from the KID API; human-readable names come from the funds API.
  // Share class names/ISINs come from allShareClasses.
  const mergedFunds = useMemo(() => {
    if (!totalFunds) return [...fundsWithKids].sort((a, b) =>
      sortFundNames(a.fund_name, b.fund_name),
    );

    const fundsMap = new Map<string, KidFund>();

    fundsWithKids.forEach((fund) => fundsMap.set(fund.fund_id, fund));

    totalFunds.forEach((totalFund) => {
      if (!fundsMap.has(totalFund.fund_id)) {
        fundsMap.set(totalFund.fund_id, {
          fund_id: totalFund.fund_id,
          fund_name: totalFund.fund_name,
          fund_name_full: totalFund.fund_name_full,
          shareClasses: [],
          editStatus: 'no-edits',
          reviewStatus: 'None to Review',
          publishedStatus: 'None to Publish',
          totalShareClasses: 0,
          completedShareClasses: 0,
        });
      } else {
        const existingFund = fundsMap.get(totalFund.fund_id)!;
        existingFund.fund_name = totalFund.fund_name;
        existingFund.fund_name_full = totalFund.fund_name_full;
      }
    });

    if (allShareClasses && allShareClasses.length > 0) {
      // Single lookup map: share_class_id → ShareClassModel
      const scById = new Map<string, ShareClassModel>(
        allShareClasses.map((sc) => [sc.share_class_id, sc]),
      );

      // Group all share classes by fund_id
      const scByFundId = new Map<string, ShareClassModel[]>();
      allShareClasses.forEach((sc) => {
        if (!scByFundId.has(sc.fund_id)) scByFundId.set(sc.fund_id, []);
        scByFundId.get(sc.fund_id)!.push(sc);
      });

      fundsMap.forEach((fund) => {
        // Enrich existing KID share class records with names and ISIN from ShareClassModel
        fund.shareClasses.forEach((sc) => {
          const model = scById.get(sc.share_class_id);
          if (model) {
            sc.share_class_isin = sc.share_class_isin || model.share_class_isin;
            sc.share_class_name = sc.share_class_name || model.share_class_name;
            sc.share_class_name_full = sc.share_class_name_full || model.share_class_name_full;
          }
        });

        // Add share classes that have no KID document yet
        const existingIds = new Set(fund.shareClasses.map((sc) => sc.share_class_id));
        const newShareClasses: KidShareClass[] = (scByFundId.get(fund.fund_id) ?? [])
          .filter((sc) => !existingIds.has(sc.share_class_id))
          .map((sc) => ({
            share_class_id: sc.share_class_id,
            share_class_isin: sc.share_class_isin,
            share_class_name: sc.share_class_name,
            share_class_name_full: sc.share_class_name_full,
            version_number: '-',
            status: KidDocumentStatus.NoDocument,
            kid_is_active: true,
            has_alert: false,
            document_language: '',
          }));

        fund.shareClasses.push(...newShareClasses);
        fund.totalShareClasses = fund.shareClasses.length;
        fund.completedShareClasses = fund.shareClasses.filter((sc) => sc.kid_is_active).length;
      });
    }

    // incomplete_documents: `invalid_overviews` = inconsistent blocks;
    // `missing_overviews` = document is missing this template (same shape, also needs surfacing).
    const templateIssueDocKeys = new Set<string>();
    const addOverviewKeys = (overviews: { fund_id: string; share_class_id: string; document_language: string }[]) => {
      overviews.forEach((ov) => {
        templateIssueDocKeys.add(
          `${ov.fund_id}|${ov.share_class_id}|${ov.document_language}`,
        );
      });
    };
    incompleteDocuments?.forEach((entry) => {
      addOverviewKeys(entry.invalid_overviews);
      addOverviewKeys(entry.missing_overviews);
    });

    const sorted = Array.from(fundsMap.values()).sort((a, b) =>
      sortFundNames(a.fund_name, b.fund_name),
    );

    if (templateIssueDocKeys.size === 0) {
      return sorted;
    }

    return sorted.map((fund) => {
      const shareClasses = fund.shareClasses.map((sc) => ({
        ...sc,
        has_shareclass_out_of_sync: templateIssueDocKeys.has(
          `${fund.fund_id}|${sc.share_class_id}|${sc.document_language}`,
        ),
      }));
      const templateIncompleteCount = shareClasses.filter(
        (sc) => sc.has_shareclass_out_of_sync,
      ).length;

      const reviewableCount = shareClasses.filter(
        (sc) =>
          sc.status === KidDocumentStatus.Edited &&
          !isShareClassBlockedByTemplateIncomplete(sc),
      ).length;
      const publishableCount = shareClasses.filter(
        (sc) =>
          sc.status === KidDocumentStatus.Reviewed &&
          !isShareClassBlockedByTemplateIncomplete(sc),
      ).length;

      return {
        ...fund,
        shareClasses,
        templateIncompleteCount,
        reviewStatus:
          reviewableCount > 0 ? `In Review (${reviewableCount})` : 'No Review',
        publishedStatus:
          publishableCount > 0
            ? `Ready to Publish (${publishableCount})`
            : fund.publishedStatus,
      };
    });
  }, [totalFunds, fundsWithKids, allShareClasses, incompleteDocuments]);

  const filteredFunds = useMemo(() => {
    if (shareClassStatusFilter === 'all') return mergedFunds;
    return mergedFunds
      .map((f) => ({
        ...f,
        shareClasses:
          shareClassStatusFilter === KidDocumentStatus.FiguresAlert
            ? f.shareClasses.filter((sc) => sc.has_alert)
            : shareClassStatusFilter === 'shareclass-out-of-sync'
              ? f.shareClasses.filter((sc) => sc.has_shareclass_out_of_sync)
              : f.shareClasses.filter(
                (sc) => sc.status === shareClassStatusFilter,
              ),
      }))
      .filter((f) => f.shareClasses.length > 0);
  }, [mergedFunds, shareClassStatusFilter]);

  // Restore expanded state and scroll position when returning from editor/review/compare
  useEffect(() => {
    if (hasRestoredRef.current) return;
    const targetFundId = (location.state as { fundId?: string } | null)?.fundId;
    if (!targetFundId || !mergedFunds.length) return;

    const rowIndex = mergedFunds.findIndex((f) => f.fund_id === targetFundId);
    if (rowIndex === -1) return;

    hasRestoredRef.current = true;
    setExpanded((prev) => ({
      ...(typeof prev === 'object' ? prev : {}),
      [String(rowIndex)]: true,
    }));

    requestAnimationFrame(() => {
      document
        .querySelector(`[data-fund-id="${targetFundId}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [mergedFunds, location.state]);

  const generateKidDocumentMutation = useGenerateKidDocument();

  const downloadFundLatestPublished = useCallback(
    async (fund: KidFund) => {
      const shareClassesWithKids = fund.shareClasses.filter(
        (sc) => sc.status !== KidDocumentStatus.NoDocument,
      );
      if (shareClassesWithKids.length === 0) return;

      for (const shareClass of shareClassesWithKids) {
        try {
          const { data: latestPublished, version } =
            await getLatestMajorPublishedVersion(
              clientId,
              fund.fund_id,
              shareClass.share_class_id,
            );

          if (!latestPublished) continue;

          const kidDataModels: UpdatedKidDataModel[] =
            (latestPublished as any).raw_kid_data_models ||
            (latestPublished as any).kid_content ||
            (latestPublished as any).kidContent ||
            (latestPublished as any).kid_data_models ||
            (latestPublished as any).kidDataModels ||
            (Array.isArray(latestPublished) ? latestPublished : []);

          if (!kidDataModels || kidDataModels.length === 0) continue;

          const elements = transformKidDataModelsToElements(kidDataModels);
          const payload: KidDocumentForm = {
            client: clientId,
            fund_name: fund.fund_name,
            fund_name_full: fund.fund_name_full,
            share_class:
              shareClass.share_class_name_full ||
              shareClass.share_class_name ||
              shareClass.share_class_id,
            date: kidDataModels[0].last_updating_timestamp,
            elements,
          };

          const response =
            await generateKidDocumentMutation.mutateAsync(payload);
          const blob = new Blob([response.data], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${[
            sanitiseFileName(fund.fund_name_full, 'fund'),
            sanitiseFileName(
              shareClass.share_class_name_full || shareClass.share_class_name,
              shareClass.share_class_id,
            ),
          ].join('-')}-${version}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } catch (error) {
          console.error(
            `Error downloading latest published for share class ${shareClass.share_class_id}:`,
            error,
          );
        }
      }
    },
    [clientId, generateKidDocumentMutation],
  );

  const fundColumns: ColumnDef<KidFund, any>[] = [
    {
      accessorKey: 'fund_name',
      header: 'Fund ID',
      meta: { align: 'left', indent: true } as any,
      cell: ({ row }) => {
        const fund = row.original as KidFund;
        const displayId = fund.fund_name;
        return (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => row.toggleExpanded()}
              className="rounded p-1 hover:bg-gray-100"
            >
              {row.getIsExpanded() ? (
                <HiChevronDown className="h-4 w-4" />
              ) : (
                <HiChevronRight className="h-4 w-4" />
              )}
            </button>
            <span className="font-medium">{displayId}</span>
          </div>
        );
      },
    },
    {
      accessorKey: 'fund_name_full',
      header: 'Fund Name',
      meta: { align: 'left' } as any,
      cell: ({ row, getValue }) => {
        const fund = row.original as KidFund;
        const figuresAlertCount = fund.shareClasses.filter((sc) => sc.has_alert).length;
        const templateIncompleteCount =
          fund.templateIncompleteCount ??
          fund.shareClasses.filter((sc) => sc.has_shareclass_out_of_sync).length;

        return (
          <span className="flex flex-wrap items-center gap-2">
            <span>{getValue()}</span>
            {figuresAlertCount > 0 && <FiguresAlert />}
            {templateIncompleteCount > 0 ? (
              <>
                <FiguresAlert variant="template-sync" />
                <Pill
                  status="fail"
                  className="whitespace-nowrap text-[10px] sm:text-xs"
                >
                  Template incomplete ({templateIncompleteCount})
                </Pill>
              </>
            ) : null}
          </span>
        );
      },
    },
    {
      accessorKey: 'shareClasses',
      header: '# Documents',
      meta: { align: 'center' } as any,
      cell: ({ row }) => {
        const fund = row.original as KidFund;
        // Count active share classes that have KID documents (status !== KidDocumentStatus.NoDocument)
        const documentCount = fund.shareClasses.filter(
          (doc) => doc.kid_is_active && doc.status !== KidDocumentStatus.NoDocument,
        ).length;

        return <span className="font-medium">{documentCount}</span>;
      },
    },
    {
      accessorKey: 'editStatus',
      header: 'Edit',
      meta: { align: 'center' } as any,
      cell: ({ row, getValue }) => {
        const fund = row.original as KidFund;
        // If fund has no share classes with KID documents, show "no-document"
        if (fund.shareClasses.length === 0) {
          return (
            <Pill
              status="na"
              className="whitespace-nowrap text-[10px] sm:text-xs"
            >
              No Document
            </Pill>
          );
        }
        const status = getValue() as KidFund['editStatus'];
        const editing = fund.shareClasses.filter(
          (sc) => sc.status === KidDocumentStatus.Editing || sc.status === KidDocumentStatus.Rejected,
        ).length;
        const label =
          status === 'editing' && editing > 0
            ? `Editing (${editing})`
            : 'No Edits';
        const pillStatus =
          status === 'editing' && editing > 0 ? 'edit' : 'neutral';
        return (
          <Pill
            status={pillStatus as any}
            className="whitespace-nowrap text-[10px] sm:text-xs"
          >
            {label}
          </Pill>
        );
      },
    },
    {
      accessorKey: 'reviewStatus',
      header: 'Review',
      meta: { align: 'center' } as any,
      cell: ({ row }) => {
        const fund = row.original as KidFund;
        // If fund has no share classes with KID documents, show "no-document"
        if (fund.shareClasses.length === 0) {
          return (
            <Pill
              status="na"
              className="whitespace-nowrap text-[10px] sm:text-xs"
            >
              No Document
            </Pill>
          );
        }
        // In Review should only show when share classes are 'edited' (ready-for-review)
        const inReview = fund.shareClasses.filter(
          (sc) =>
            sc.status === KidDocumentStatus.Edited &&
            !isShareClassBlockedByTemplateIncomplete(sc),
        ).length;
        const label = inReview > 0 ? `In Review (${inReview})` : 'No Review';
        // Use light purple for review
        const pillStatus = inReview > 0 ? 'review' : 'neutral';
        return (
          <Pill
            status={pillStatus as any}
            className="whitespace-nowrap text-[10px] sm:text-xs"
          >
            {label}
          </Pill>
        );
      },
    },
    {
      accessorKey: 'publishedStatus',
      header: 'Publish',
      meta: { align: 'center' } as any,
      cell: ({ row, getValue }) => {
        const fund = row.original as KidFund;
        // If fund has no share classes with KID documents, show "no-document"
        if (fund.shareClasses.length === 0) {
          return (
            <Pill
              status="na"
              className="whitespace-nowrap text-[10px] sm:text-xs"
            >
              No Document
            </Pill>
          );
        }
        const label = getValue() as string;
        const pillStatus =
          label === 'Published' || label.startsWith('Ready to Publish')
            ? 'pass'
            : 'na';

        return (
          <Pill
            status={pillStatus as any}
            className="whitespace-nowrap text-[10px] sm:text-xs"
          >
            {label}
          </Pill>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      meta: { align: 'center' } as any,
      cell: ({ row }) => {
        const fund = row.original as KidFund;
        const shareClassesWithKids = fund.shareClasses.filter(
          (sc) => sc.status !== KidDocumentStatus.NoDocument,
        );
        const hasKids = shareClassesWithKids.length > 0;
        return (
          <div className="flex items-center justify-center">
            <IconButton
              variant="download"
              tooltip="Download latest Published (all share classes with KIDs)"
              disabled={!hasKids}
              onClick={() => downloadFundLatestPublished(fund)}
            />
          </div>
        );
      },
    },
  ];


  const table = useReactTable({
    data: filteredFunds,
    columns: fundColumns,
    filterFns: {
      nestedFuzzy: nestedFuzzyFilter,
    },
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      globalFilter: fundSearch,
      expanded,
    },
    onGlobalFilterChange: setFundSearch,
    globalFilterFn: 'nestedFuzzy', // Use nested fuzzy filter for global search
    onExpandedChange: setExpanded,
  } as any);

  // Toggle the control bar to be shown
  useEffect(() => {
    dispatch(
      updateControlBarComponents([
        <div className="flex items-center justify-between px-4">
          <div className="flex items-center space-x-6">
            <h1 className="text-2xl font-semibold text-mainblue">
              PRIIPs KID Overview
            </h1>
            <div className="flex items-center space-x-3">
              <div className="inline-flex items-stretch overflow-hidden rounded-md border border-gray-300">
                <label
                  htmlFor="jurisdiction-select"
                  className="select-none border-r border-gray-300 bg-mainblue px-2 py-1 text-sm font-medium text-white"
                >
                  Jurisdiction
                </label>
                <select
                  id="jurisdiction-select"
                  value={selectedJurisdiction}
                  onChange={(e) => setSelectedJurisdiction(e.target.value)}
                  disabled={isLoadingJurisdictions}
                  className={
                    'border-0 bg-white p-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500' +
                    (isLoadingJurisdictions
                      ? ' cursor-not-allowed opacity-50'
                      : '')
                  }
                  aria-describedby="jurisdiction-select"
                >
                  <option value="">
                    {isLoadingJurisdictions ? 'Loading...' : 'Select jurisdiction'}
                  </option>
                  {jurisdictionOptions.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="inline-flex items-stretch overflow-hidden rounded-md border border-gray-300">
                <label
                  htmlFor="language-select"
                  className="select-none border-r border-gray-300 bg-mainblue px-2 py-1 text-sm font-medium text-white"
                >
                  Language
                </label>
                <select
                  id="language-select"
                  value={selectedLanguage}
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                  disabled={isLoadingLanguages}
                  className={
                    'border-0 bg-white p-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500' +
                    (isLoadingLanguages ? ' cursor-not-allowed opacity-50' : '')
                  }
                  aria-describedby="language-select"
                >
                  <option value="">
                    {isLoadingLanguages ? 'Loading...' : 'Select language'}
                  </option>
                  {languages?.map((language) => (
                    <option key={language} value={language}>
                      {language}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>,
      ]),
    );
    dispatch(toggleControlBarShowing(true));

    return () => {
      // Cleanup function to reset control bar components and hide the control bar
      dispatch(updateControlBarComponents([]));
      dispatch(toggleControlBarShowing(false));
    };
  }, [
    selectedJurisdiction,
    selectedLanguage,
    jurisdictionOptions,
    languages,
    isLoadingJurisdictions,
    isLoadingLanguages,
  ]);

  return (
    <div className="-m-2.5 flex h-[calc(100vh-8.75rem)] scrollbar-custom w-[calc(100%+1.25rem)] overflow-y-auto">
      <div className="w-full px-4 pt-3">
        <KidToolBar
          funds={mergedFunds}
          isLoading={isLoading}
          isError={isError}
        />

        <div className="flex">
          {/* Main Content */}
          <div className="flex-1">
            {/* Fund Overview Table */}
            <div className="my-3 rounded-lg border border-gray-200 bg-white shadow-sm">
              <div className="border-b border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-mainblue">
                    Fund Overview
                  </h2>
                  <div className="flex items-center space-x-4">
                    <SearchBar
                      searchHandler={setFundSearch}
                      placeholder="Search Documents..."
                    />
                    <select
                      value={shareClassStatusFilter}
                      onChange={(e) =>
                        setShareClassStatusFilter(e.target.value as any)
                      }
                      className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="all">All Status</option>
                      <option value="published">Published</option>
                      <option value="reviewed">Ready to Publish</option>
                      <option value="edited">Edited</option>
                      <option value="no-document">No Document</option>
                      <option value="deactivated">Deactivated</option>
                      <option value="figures-alert">Figures Alert</option>
                      <option value="shareclass-out-of-sync">
                        Template incomplete (missing / invalid)
                      </option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-mainblue text-white">
                    <tr>
                      {table.getHeaderGroups().map((headerGroup) =>
                        headerGroup.headers.map((header) => (
                          <th
                            key={header.id}
                            className={(() => {
                              const meta = header.column.columnDef.meta as ColumnAlignMeta;
                              if (meta?.align === 'center') return 'px-4 py-3 text-center text-sm font-medium';
                              if (meta?.indent) return 'pl-12 pr-4 py-3 text-left text-sm font-medium';
                              return 'px-4 py-3 text-left text-sm font-medium';
                            })()}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                          </th>
                        )),
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <SkeletonLoader
                        variant="table"
                        count={12}
                        columnCount={fundColumns.length}
                      />
                    ) : isError ? (
                      <tr>
                        <td
                          colSpan={fundColumns.length}
                          className="px-4 py-12 text-center"
                        >
                          <div className="flex flex-col items-center justify-center">
                            <p className="text-lg font-semibold text-red-600">
                              Failed to load overview data
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : table.getRowModel().rows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={fundColumns.length}
                          className="px-4 py-12 text-center text-gray-500"
                        >
                          No funds found
                        </td>
                      </tr>
                    ) : (
                      table.getRowModel().rows.map((row) => {
                        const fund = row.original as KidFund;
                        return (
                          <>
                            <tr
                              key={row.id}
                              data-fund-id={fund.fund_id}
                              className="border-b border-gray-200 hover:bg-gray-50"
                            >
                              {row.getVisibleCells().map((cell) => (
                                <td key={cell.id} className={`px-4 py-3 text-sm${(cell.column.columnDef.meta as ColumnAlignMeta)?.align === 'center' ? ' text-center' : ''}`}>
                                  {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext(),
                                  )}
                                </td>
                              ))}
                            </tr>
                            {row.getIsExpanded() && (
                              <ShareClassTableRow
                                fund={row.original as KidFund}
                                colSpan={fundColumns.length}
                                selectedJurisdiction={selectedJurisdiction}
                                selectedLanguage={selectedLanguage}
                                highlightShareClassId={
                                  fund.fund_id === (location.state as { fundId?: string } | null)?.fundId
                                    ? (location.state as { shareClassId?: string } | null)?.shareClassId
                                    : undefined
                                }
                              />
                            )}
                          </>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KidsOverviewV2Page;
